import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import TerminosPage from "./pages/TerminosPage";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./pages/DashboardHome";
import DefinicionesPage from "./pages/DefinicionesPage";
import ContribuyentesPage from "./pages/ContribuyentesPage";
import AseoUrbanoPage from "./pages/AseoUrbanoPage";
import LiquidacionesPage from "./pages/LiquidacionesPage";
import RecaudacionesPage from "./pages/RecaudacionesPage";
import ReclamosPage from "./pages/ReclamosPage";
import ConfiguracionPage from "./pages/ConfiguracionPage";
import AdminUsuariosPage from "./pages/AdminUsuariosPage";
import UsuariosContribuyentesPage from "./pages/UsuariosContribuyentesPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RolesPage from "./pages/RolesPage";
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalDeudaPage from "./pages/portal/PortalDeudaPage";
import PortalHistorialPage from "./pages/portal/PortalHistorialPage";
import PortalReclamosPage from "./pages/portal/PortalReclamosPage";
import PortalReportarPagoPage from "./pages/portal/PortalReportarPagoPage";
import PortalSolvenciaPage from "./pages/portal/PortalSolvenciaPage";
import PortalManualPage from "./pages/portal/PortalManualPage";
import SolvenciasPage from "./pages/SolvenciasPage";
import ConciliacionBancariaPage from "./pages/ConciliacionBancariaPage";
import MantenimientoPage from "./pages/MantenimientoPage";
import ReportesPage from "./pages/ReportesPage";
import ReporteCuentasPage from "./pages/ReporteCuentasPage";
import ReporteCanalPage from "./pages/ReporteCanalPage";
import ManualesPage from "./pages/ManualesPage";
import AuditoriaPage from "./pages/AuditoriaPage";
import ReporteCuentasPorCobrarPage from "./pages/ReporteCuentasPorCobrarPage";
import VerificarSolvenciaPage from "./pages/VerificarSolvenciaPage";
import PagarPlanillaPage from "./pages/PagarPlanillaPage";
import VerificarComprobantePage from "./pages/VerificarComprobantePage";
import ChangePasswordDialog from "./components/ChangePasswordDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem("sisaur_token"));
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (token) {
        try {
          const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          setUser(r.data);
        } catch {
          sessionStorage.removeItem("sisaur_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    init();
  }, [token]);

  const login = async (email, password, user_type) => {
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password, user_type });
      sessionStorage.setItem("sisaur_token", r.data.access_token);
      setToken(r.data.access_token);
      setUser(r.data.user);
      setMustChangePassword(!!r.data.must_change_password);
      toast.success(`Bienvenido, ${r.data.user.nombre}`);
      return { ok: true, user: r.data.user, mustChangePassword: !!r.data.must_change_password };
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al iniciar sesión";
      toast.error(typeof msg === "string" ? msg : "Error al iniciar sesión");
      return { ok: false };
    }
  };

  const register = async (data) => {
    try {
      const r = await axios.post(`${API}/auth/register`, data);
      sessionStorage.setItem("sisaur_token", r.data.access_token);
      setToken(r.data.access_token);
      setUser(r.data.user);
      toast.success("Cuenta creada exitosamente");
      return { ok: true };
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al registrar";
      toast.error(typeof msg === "string" ? msg : "Error al registrar");
      return { ok: false };
    }
  };

  const logout = () => {
    sessionStorage.removeItem("sisaur_token");
    setToken(null);
    setUser(null);
    toast.info("Sesión cerrada");
  };

  const apiHeaders = () => ({ Authorization: `Bearer ${token}` });

  const dismissChangePassword = () => setMustChangePassword(false);

  /**
   * Verifica si el usuario tiene un permiso específico o cualquiera de una lista.
   * Uso: hasPermission("recaudaciones.validar") o hasPermission(["recaudaciones.validar","recaudaciones.anular"])
   * Superadmin siempre pasa. Si aún no cargamos permisos (durante boot), permite (evita flicker).
   */
  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    // Si el backend aún no envió permisos (por ejemplo post-login inmediato), no bloquear
    if (!Array.isArray(user.permissions)) return true;
    const list = Array.isArray(perm) ? perm : [perm];
    return list.some(p => user.permissions.includes(p));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, apiHeaders, API, mustChangePassword, dismissChangePassword, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAF6]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-700 border-t-orange-500 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-green-900 font-medium">Cargando SISAUR…</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "contribuyente" ? "/portal" : "/dashboard"} replace />;
  }
  return children;
};

const HomeRedirect = () => {
  const { user } = useAuth();
  if (!user) return <LandingPage />;
  return <Navigate to={user.role === "contribuyente" ? "/portal" : "/dashboard"} replace />;
};

function ForcedPasswordWatcher() {
  const { mustChangePassword, dismissChangePassword, user } = useAuth();
  if (!user) return null;
  return <ChangePasswordDialog open={mustChangePassword} forced onSuccess={dismissChangePassword} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <ForcedPasswordWatcher />
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/contribuyente" element={<LoginPage userType="contribuyente" />} />
          <Route path="/login/funcionario" element={<LoginPage userType="funcionario" />} />
          <Route path="/registro" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terminos" element={<TerminosPage />} />
          <Route path="/verificar-solvencia/*" element={<VerificarSolvenciaPage />} />
          <Route path="/verificar-comprobante/*" element={<VerificarComprobantePage />} />
          <Route path="/pagar/*" element={<PagarPlanillaPage />} />

          {/* Backoffice — funcionarios */}
          <Route element={<ProtectedRoute allowedRoles={["superadmin", "admin_financiero", "operador"]}><DashboardLayout mode="funcionario" /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/definiciones" element={<DefinicionesPage />} />
            <Route path="/contribuyentes" element={<ContribuyentesPage />} />
            <Route path="/aseo-urbano" element={<AseoUrbanoPage />} />
            <Route path="/liquidaciones" element={<LiquidacionesPage />} />
            <Route path="/recaudaciones" element={<RecaudacionesPage />} />
            <Route path="/conciliacion" element={<ConciliacionBancariaPage />} />
            <Route path="/solvencias" element={<SolvenciasPage />} />
            <Route path="/mantenimiento" element={<MantenimientoPage />} />
            <Route path="/reportes" element={<ReportesPage />} />
            <Route path="/reportes/actividad" element={<ReportesPage />} />
            <Route path="/reportes/cuentas" element={<ReporteCuentasPage />} />
            <Route path="/reportes/por-cobrar" element={<ReporteCuentasPorCobrarPage />} />
            <Route path="/reportes/canal" element={<ReporteCanalPage />} />
            <Route path="/manuales" element={<ManualesPage />} />
            <Route path="/reclamos" element={<ReclamosPage />} />
            <Route path="/configuracion" element={<ConfiguracionPage />} />
            <Route path="/admin/usuarios" element={<AdminUsuariosPage />} />
            <Route path="/admin/usuarios-contribuyentes" element={<UsuariosContribuyentesPage />} />
            <Route path="/admin/roles" element={<RolesPage />} />
            <Route path="/admin/auditoria" element={<AuditoriaPage />} />
          </Route>

          {/* Portal — contribuyentes */}
          <Route element={<ProtectedRoute allowedRoles={["contribuyente"]}><DashboardLayout mode="contribuyente" /></ProtectedRoute>}>
            <Route path="/portal" element={<PortalDashboard />} />
            <Route path="/portal/deuda" element={<PortalDeudaPage />} />
            <Route path="/portal/historial" element={<PortalHistorialPage />} />
            <Route path="/portal/reclamos" element={<PortalReclamosPage />} />
            <Route path="/portal/reportar-pago" element={<PortalReportarPagoPage />} />
            <Route path="/portal/solvencia" element={<PortalSolvenciaPage />} />
            <Route path="/portal/manual" element={<PortalManualPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
